import { useState } from 'react'
import { useSchoolVehicles, type VehicleTransmission } from '@/hooks/useSchoolVehicles'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'
import { SchoolSwitcher } from '@/components/school/SchoolSwitcher'
import { CarIcon } from '@/components/icons/CarIcon'

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/

const fieldLabel: React.CSSProperties = {
  font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', display: 'block', marginBottom: 6,
}

interface FormState {
  make: string; model: string; year: string; colour: string; kmRun: string
  licencePlate: string; vin: string; transmission: VehicleTransmission | ''
  photoPath: string | null
}

const EMPTY_FORM: FormState = {
  make: '', model: '', year: '', colour: '', kmRun: '', licencePlate: '', vin: '', transmission: '', photoPath: null,
}

function VehicleForm({ schoolId, initial, onDone, onCancel }: {
  schoolId: string
  initial: { id: string | null; form: FormState; isActive: boolean }
  onDone: () => void
  onCancel: () => void
}) {
  const { saving, uploadingPhoto, createVehicle, updateVehicle, uploadVehiclePhoto, photoUrl } = useSchoolVehicles(schoolId)
  const [form, setForm] = useState<FormState>(initial.form)
  const [isActive, setIsActive] = useState(initial.isActive)
  const [error, setError] = useState('')

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const result = await uploadVehiclePhoto(file)
    if (!result.ok) { setError('Could not upload the photo. Please try again.'); return }
    setField('photoPath', result.path ?? null)
  }

  async function handleSubmit() {
    setError('')
    if (!form.make.trim()) { setError('Make is required.'); return }
    if (!form.model.trim()) { setError('Model is required.'); return }
    if (!VIN_PATTERN.test(form.vin.trim().toUpperCase())) {
      setError('Enter a valid 17-character VIN (letters and digits, no I, O or Q).')
      return
    }

    const fields = {
      make: form.make.trim(),
      model: form.model.trim(),
      year: form.year ? Number(form.year) : null,
      colour: form.colour.trim() || null,
      kmRun: form.kmRun ? Number(form.kmRun) : null,
      licencePlate: form.licencePlate.trim() || null,
      vin: form.vin.trim().toUpperCase(),
      transmission: form.transmission || null,
      photoPath: form.photoPath,
    }

    const result = initial.id
      ? await updateVehicle(initial.id, { ...fields, isActive })
      : await createVehicle(fields)

    if (!result.ok) { setError('Could not save this vehicle. Please try again.'); return }
    onDone()
  }

  const vehiclePhotoUrl = photoUrl(form.photoPath)

  return (
    <div style={{ background: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 10, overflow: 'hidden', flexShrink: 0,
          background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {vehiclePhotoUrl ? (
            <img src={vehiclePhotoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <CarIcon style={{ width: 28, height: 28, color: 'rgba(27,42,74,0.25)' }} />
          )}
        </div>
        <label style={{
          display: 'inline-block', padding: '8px 16px', background: '#fff',
          border: '1px solid rgba(27,42,74,0.15)', borderRadius: 8,
          font: '600 12.5px Inter, sans-serif', color: '#1B2A4A',
          cursor: uploadingPhoto ? 'not-allowed' : 'pointer', opacity: uploadingPhoto ? 0.6 : 1,
        }}>
          {uploadingPhoto ? 'Uploading…' : form.photoPath ? 'Replace photo' : 'Upload photo'}
          <input type='file' accept='image/*' onChange={handlePhotoChange} disabled={uploadingPhoto} style={{ display: 'none' }} />
        </label>
      </div>

      <div className='fg3' style={{ marginBottom: 14 }}>
        <div>
          <div style={fieldLabel}>Make</div>
          <input value={form.make} onChange={e => setField('make', e.target.value)} className='field-input' disabled={saving} />
        </div>
        <div>
          <div style={fieldLabel}>Model</div>
          <input value={form.model} onChange={e => setField('model', e.target.value)} className='field-input' disabled={saving} />
        </div>
        <div>
          <div style={fieldLabel}>Year</div>
          <input type='number' min='1980' max='2100' value={form.year} onChange={e => setField('year', e.target.value)} className='field-input' disabled={saving} />
        </div>
      </div>
      <div className='fg3' style={{ marginBottom: 14 }}>
        <div>
          <div style={fieldLabel}>Colour</div>
          <input value={form.colour} onChange={e => setField('colour', e.target.value)} className='field-input' disabled={saving} />
        </div>
        <div>
          <div style={fieldLabel}>Transmission</div>
          <select value={form.transmission} onChange={e => setField('transmission', e.target.value as VehicleTransmission | '')} className='field-input' disabled={saving}>
            <option value=''>Not set</option>
            <option value='automatic'>Automatic</option>
            <option value='manual'>Manual</option>
          </select>
        </div>
        <div>
          <div style={fieldLabel}>Km run (optional)</div>
          <input type='number' min='0' step='0.1' value={form.kmRun} onChange={e => setField('kmRun', e.target.value)} className='field-input' disabled={saving} />
        </div>
      </div>
      <div className='fg2' style={{ marginBottom: 14 }}>
        <div>
          <div style={fieldLabel}>Licence plate</div>
          <input value={form.licencePlate} onChange={e => setField('licencePlate', e.target.value)} className='field-input' disabled={saving} />
        </div>
        <div>
          <div style={fieldLabel}>VIN</div>
          <input value={form.vin} onChange={e => setField('vin', e.target.value.toUpperCase())} maxLength={17} placeholder='17 characters' className='field-input' disabled={saving} />
        </div>
      </div>

      {initial.id && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, font: '500 13px Inter, sans-serif', color: '#1B2A4A', marginBottom: 14, cursor: 'pointer' }}>
          <input type='checkbox' checked={isActive} onChange={e => setIsActive(e.target.checked)} disabled={saving} />
          Active (available to assign)
        </label>
      )}

      {error && <div className='banner-error' style={{ marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={handleSubmit} disabled={saving} className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }}>
          {saving ? 'Saving…' : initial.id ? 'Save changes' : 'Add vehicle'}
        </button>
        <button
          onClick={onCancel}
          style={{ background: 'none', border: 'none', font: '600 13px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default function Fleet() {
  const { selectedSchoolId } = useSelectedSchool()
  const { vehicles, loading, error, photoUrl, refresh, releaseVehicle, saving } = useSchoolVehicles(selectedSchoolId)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [releasingId, setReleasingId] = useState<string | null>(null)

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>Fleet</div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 6 }}>
        Cars your school owns and assigns to employees — not self-reported by the instructor.
      </div>

      <SchoolSwitcher />

      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '18px 22px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: adding ? 16 : 0 }}>
          <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A' }}>Add a vehicle</div>
          <button onClick={() => setAdding(v => !v)} className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }}>
            {adding ? 'Cancel' : '+ Add vehicle'}
          </button>
        </div>
        {adding && selectedSchoolId && (
          <VehicleForm
            schoolId={selectedSchoolId}
            initial={{ id: null, form: EMPTY_FORM, isActive: true }}
            onDone={() => { setAdding(false); refresh() }}
            onCancel={() => setAdding(false)}
          />
        )}
      </div>

      {loading && (
        <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
      )}

      {error && !loading && <div className='banner-error' style={{ marginBottom: 16 }}>{error}</div>}

      {!loading && !error && vehicles.length === 0 && (
        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '36px 28px', textAlign: 'center' }}>
          <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 8 }}>No vehicles yet</div>
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            Add a car above so you can assign it to an employee.
          </div>
        </div>
      )}

      {!loading && selectedSchoolId && vehicles.map(v => {
        const url = photoUrl(v.photoPath)
        const isEditing = editingId === v.id
        return (
          <div key={v.id} style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '16px 20px', marginBottom: 14, opacity: v.isActive ? 1 : 0.6 }}>
            {isEditing ? (
              <VehicleForm
                schoolId={selectedSchoolId}
                initial={{
                  id: v.id,
                  form: {
                    make: v.make, model: v.model, year: v.year === null ? '' : String(v.year),
                    colour: v.colour ?? '', kmRun: v.kmRun === null ? '' : String(v.kmRun),
                    licencePlate: v.licencePlate ?? '', vin: v.vin ?? '', transmission: v.transmission ?? '',
                    photoPath: v.photoPath,
                  },
                  isActive: v.isActive,
                }}
                onDone={() => { setEditingId(null); refresh() }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                    background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {url
                      ? <img src={url} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <CarIcon style={{ width: 24, height: 24, color: 'rgba(27,42,74,0.25)' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                      {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                      {!v.isActive && <span style={{ marginLeft: 8, font: '600 11px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>ARCHIVED</span>}
                    </div>
                    <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
                      {[v.colour, v.transmission, v.licencePlate].filter(Boolean).join(' · ') || '—'}
                    </div>
                    {v.vin && (
                      <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                        VIN: {v.vin}
                      </div>
                    )}
                    {v.assignedToName && (
                      <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(0,128,128,0.75)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Assigned to: {v.assignedToName}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {v.assignedToMembershipId && (
                      <button
                        onClick={async () => {
                          if (!v.assignedToMembershipId || releasingId === v.id || saving) return
                          if (!confirm(`Release this vehicle from ${v.assignedToName}?`)) return
                          setReleasingId(v.id)
                          const result = await releaseVehicle(v.assignedToMembershipId)
                          setReleasingId(null)
                          if (!result.ok) alert('Could not release this vehicle. Please try again.')
                        }}
                        disabled={releasingId === v.id || saving}
                        style={{
                          padding: '6px 14px', background: 'transparent', border: '1px solid rgba(255,69,58,0.3)',
                          borderRadius: 7, font: '500 12px Inter, sans-serif', color: '#FF453A',
                          cursor: (releasingId === v.id || saving) ? 'not-allowed' : 'pointer',
                          opacity: (releasingId === v.id || saving) ? 0.6 : 1,
                        }}
                      >
                        {releasingId === v.id ? 'Releasing…' : 'Release'}
                      </button>
                    )}
                    <button
                      onClick={() => setEditingId(v.id)}
                      style={{
                        padding: '6px 14px', background: 'transparent', border: '1px solid rgba(27,42,74,0.15)',
                        borderRadius: 7, font: '500 12px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
